import {captureException} from '@sentry/browser';
import {
  DataStatus,
  FormControl,
  FormField,
  OptionType,
  showNotification,
} from 'platform/components';
import {Grid, GridItem, Show} from 'platform/foundation';

import {useState} from 'react';
import {UseFormReturn} from 'react-hook-form';

import {isNilOrEmpty, isNotNilOrEmpty, isString} from 'ramda-adjunct';

import {CustomerMessageIdEnum} from '@dms/api/core';
import {useLazyCustomerAdvancedSearchQuery, useLazyGetCustomerV2Query} from '@dms/api/customer';
import i18n from '@dms/i18n';

import {useToggle} from 'shared/web';

import {getCustomerName} from '../../../utils/getCustomerName';
import {EMAIL_REGEX} from '../constants/EMAIL_REGEX';
import {useSmtpOptions} from '../hooks/useSmtpOptions';
import {FormValues} from '../types/SendCustomerEmailFormSchema';
import {getAdvancedCustomerEmails} from '../utils/getAdvancedCustomerEmails';
import {getAdvancedCustomerName} from '../utils/getAdvancedCustomerName';
import {getCustomerEmails} from '../utils/getCustomerEmails';

interface CustomerSelectChoiceProps {
  control: FormControl<FormValues>;
  formApi: UseFormReturn<FormValues>;
  customerMessageId: CustomerMessageIdEnum;
  defaultOptions?: OptionType[];
}

export function CustomerSelectField(props: CustomerSelectChoiceProps) {
  const [getCustomers, {data: loadedCustomers}] = useLazyCustomerAdvancedSearchQuery();
  const {smtpCredentialsOptions} = useSmtpOptions(props.customerMessageId);
  const [getCustomer] = useLazyGetCustomerV2Query();

  const [customerEmailOptions, setCustomerEmailOptions] = useState<OptionType[]>([]);
  const [copyToOptions, setCopyToOptions] = useState<OptionType[]>([]);

  const [isCopyToVisible, toggleCopyTo] = useToggle();

  const defaultOptions = props.defaultOptions ?? [];

  return (
    <DataStatus minHeight={14}>
      <Grid columns={2} verticalSpacing={2}>
        <GridItem span={2}>
          <FormField
            control={props.control}
            name="smtpCredentialsId"
            type="choice"
            label={i18n.t('entity.customer.labels.sender')}
            options={smtpCredentialsOptions}
            isNotClearable
            isRequired
          />
        </GridItem>
        <FormField
          control={props.control}
          name="customerId"
          type="asyncChoice"
          tooltip={
            isNotNilOrEmpty(defaultOptions)
              ? i18n.t('entity.customer.labels.defaultCustomersTooltip')
              : undefined
          }
          label={i18n.t('entity.customer.labels.customer')}
          noOptionsMessage={() => i18n.t('entity.customer.labels.typeToSearch')}
          isRequired
          loadLabels={async (customerId) => {
            if (!isString(customerId)) {
              setCustomerEmailOptions([]);
              return [];
            }

            const alreadyLoadedCustomer = loadedCustomers?.find(
              (customer) => customer.id === customerId
            );

            if (alreadyLoadedCustomer) {
              const customerEmails = getAdvancedCustomerEmails(alreadyLoadedCustomer);
              setCustomerEmailOptions(customerEmails);
              props.formApi.setValue('customerEmailAddress', customerEmails[0]?.value);
              return [{value: customerId, label: getAdvancedCustomerName(alreadyLoadedCustomer)}];
            }

            try {
              const customer = await getCustomer({customerId}).unwrap();
              const customerEmails = getCustomerEmails(customer);
              props.formApi.setValue('customerEmailAddress', customerEmails[0]?.value);

              setCustomerEmailOptions(customerEmails);
              return [{value: customerId, label: getCustomerName(customer)}];
            } catch (error) {
              captureException(error);
              setCustomerEmailOptions([]);
              showNotification.error();
              return [];
            }
          }}
          loadOptions={async (name) => {
            if (isNilOrEmpty(name) || !isString(name)) {
              return defaultOptions;
            }

            try {
              const customers = await getCustomers({name}).unwrap();

              if (isNotNilOrEmpty(defaultOptions)) {
                return [
                  {
                    label: i18n.t('entity.serviceOrder.labels.defaultCustomers'),
                    options: defaultOptions,
                  },
                  {
                    label: i18n.t('entity.customer.labels.searchedCustomers'),
                    options: customers.map((customer) => ({
                      label: getAdvancedCustomerName(customer),
                      value: customer.id,
                    })),
                  },
                ];
              }

              return customers.map((customer) => ({
                label: getAdvancedCustomerName(customer),
                value: customer.id,
              }));
            } catch (error) {
              captureException(error);
              showNotification.error();
              return defaultOptions;
            }
          }}
          isNotClearable
        />

        <FormField
          control={props.control}
          name="customerEmailAddress"
          type="creatableChoice"
          labelActions={[
            {
              title: i18n.t('general.labels.sendCopyTo'),
              leftIcon: 'content/add_circle_outline',
              onClick: toggleCopyTo,
            },
          ]}
          label={i18n.t('general.labels.emailAddress')}
          isRequired
          isNotClearable
          onChange={() => props.formApi.clearErrors('customerEmailAddress')}
          options={customerEmailOptions}
          onCreateOption={(value) => {
            if (!EMAIL_REGEX.test(value)) {
              props.formApi.setError('customerEmailAddress', {
                message: i18n.t('errors.invalid_string.email'),
              });
              return;
            }

            setCustomerEmailOptions((prevState) => {
              if (prevState.some((option) => option.value === value)) {
                return prevState;
              }

              return [...prevState, {label: value, value}];
            });
            props.formApi.setValue('customerEmailAddress', value);
          }}
        />

        <Show when={isCopyToVisible}>
          <GridItem span={2}>
            <FormField
              control={props.control}
              name="copyTo"
              type="creatableMultiChoice"
              label={i18n.t('general.labels.sendCopyTo')}
              options={copyToOptions}
              placeholder={i18n.t('entity.email.labels.email')}
              noOptionsMessage={() => i18n.t('entity.customer.labels.typeToAddEmail')}
              onCreateOption={(value) => {
                setCopyToOptions((prevState) => {
                  if (prevState.some((option) => option.value === value)) {
                    return prevState;
                  }
                  return [...prevState, {label: value, value}];
                });

                props.formApi.setValue('copyTo', [
                  ...(props.formApi.getValues('copyTo') ?? []),
                  value,
                ]);
              }}
            />
          </GridItem>
        </Show>
      </Grid>
    </DataStatus>
  );
}
